import React, { useState, useEffect } from 'react';
import {
  Stack, Typography, Box, Radio, RadioGroup, FormControlLabel, Icon,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { useForm, useWatch } from 'react-hook-form';
import { useFetch } from 'use-http';
import BaseModal from '../../../../common/atoms/BaseModal/BaseModal';
import { usePhotoGallery } from '../../../../hooks/usePhotoGallery';
import LoadingSpinner from '../../../../components/generic/LoadingSpinner';
import { useAuthContext } from '../../../../providers/auth-provider';
import StyledTextField from '../../../../common/atoms/inputs/StyledTextField';
import StyledTextArea from '../../../../common/atoms/inputs/StyledTextarea';
import StyledDropdown from '../../../../common/atoms/inputs/StyledDropdown';
import BackrsButton from '../../../../common/atoms/backrsButton/BackrsButton';
import { useAppState } from '../../../../providers/app-state/app-state-provider';
import appStrings from '../../../../common/app_strings';
import appColors from '../../../../common/app_colors';
import { handleError, handleMediaError } from './utils';

const StoryUploadModal = ({
  isOpen,
  handleClose,
  questionId,
  onStoryChange,
}) => {
  const { control, handleSubmit, reset } = useForm();
  const [media, setMedia] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState('text');
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const { loading, takeVideo, takeWebVideo } = usePhotoGallery();
  const { user } = useAuthContext();
  const { platform: { isNativePlatform } } = useAppState();
  const { post, response } = useFetch();
  const { wholeMeProfileStory: { modal, errors } } = appStrings;
  const { buttons, text } = appColors;
  const watchTitle = useWatch({ control, name: 'title' });
  const watchDescription = useWatch({ control, name: 'description' });
  const descriptionMaxLength = 50;
  const descriptionWatchLength = watchDescription?.length;

  const handleMobileVideo = async () => {
    if (loading) return;
    try {
      const videoObj = await takeVideo();
      if (videoObj?.media) {
        const { webviewPath, media: video } = videoObj;
        // infer filename from mobile video upload
        const filename = webviewPath?.substring(webviewPath.lastIndexOf('/') + 1);
        setMedia({ webviewPath: { name: filename }, id: video?.id, type: 'video' });
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const handleWebVideo = async () => {
    if (loading) return;
    const videoObj = await takeWebVideo();
    if (videoObj?.media) {
      const { webviewPath, media: video } = videoObj;
      setMedia({ webviewPath, id: video?.id, type: 'video' });
    }
  };

  const handleVideo = async () => {
    if (loading) return;
    setIsLoading(true);
    const uploader = isNativePlatform ? handleMobileVideo : handleWebVideo;
    try {
      await uploader();
    } catch (e) {
      handleClose();
      handleMediaError(e, errors.video);
    } finally {
      setIsLoading(false);
    }
  };

  const saveToProfile = async (data, mediaData) => {
    const payload = {
      category_id: 'story',
      title: data?.title,
      question_id: questionId,
      description: data?.description,
      media_id: mediaData?.id,
      visibility: data?.visibility,
      user_id: user?.id,
      media_type: mediaData?.type || selectedOption,
    };

    try {
      setIsLoading(true);
      const res = await post('/users-profile-answers/', payload);
      if (!response.ok) {
        console.error(`${modal.failedToSave}`, response);
        if (response.status === 400 && response?.data?.error === errors.characterLimit) {
          throw new Error(errors.characterLimit);
        }
        throw new Error(modal.failedToSave);
      }
      reset();
      if (selectedOption === 'text' || selectedOption === 'video') { onStoryChange(); }
      return res;
    } catch (error) {
      console.error(modal.failedToSave, error);
      Sentry.captureException(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitForm = async (data) => {
    try {
      await saveToProfile(data, media);
    } catch (e) {
      Sentry.captureException(e);
      handleError(e);
    } finally {
      setMedia(null);
      handleClose();
    }
  };

  const handleCloseModal = () => {
    reset();
    setMedia(null);
    handleClose();
  };

  useEffect(() => {
    const isDisabled = (
      selectedOption === errors.video && !media?.id)
 || !watchTitle
 || (selectedOption === errors.video && descriptionWatchLength > descriptionMaxLength);
    setIsSubmitDisabled(isDisabled);
  }, [media, watchTitle, selectedOption, watchDescription]);

  const renderDynamicFields = () => (
    isLoading ? <LoadingSpinner /> : (
      <>
        <Stack
          sx={{
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            pb: '10px',
          }}
        >
          <Typography
            variant="h7"
            sx={{
              fontWeight: 500,
              fontSize: '16px',
            }}
          >
            {modal.createAStory}
          </Typography>
        </Stack>
        <RadioGroup
          value={selectedOption}
          onChange={(e) => setSelectedOption(e.target.value)}
          row
        >
          <FormControlLabel value="text" control={<Radio />} label="Text" />
          <FormControlLabel value="video" control={<Radio />} label="Video" />
        </RadioGroup>
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <Stack spacing={2}>
            <StyledTextField
              control={control}
              defaultValue=""
              name="title"
              placeholder={modal.title}
              required
            />
            <Stack spacing={0}>
              <StyledTextArea
                control={control}
                defaultValue=""
                name="description"
                placeholder={modal.description}
                maxCharacters={selectedOption === 'video' ? 50 : null}
              />
              {selectedOption === 'video' && (
                <Typography sx={{
                  fontSize: '13px',
                  overflow: 'hidden',
                  color: (descriptionWatchLength > descriptionMaxLength) ? text.red : text.grey,
                  textAlign: 'right',
                }}
                >
                  {modal.characterLimit(descriptionWatchLength || 0)}
                </Typography>
              )}
            </Stack>
            <StyledDropdown
              control={control}
              name="visibility"
              required
              placeholder={modal.whoCanSee}
              defaultValue="public"
              choices={[
                { value: 'panel', label: modal.panel },
                { value: 'public', label: modal.public },
              ]}
            />
            {media && (
            <Typography sx={{
              fontSize: '16px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: text.darkNavy,
              fontWeight: '700',
            }}
            >
              <Icon
                sx={{
                  position: 'relative',
                  overflow: 'visible',
                  transform: 'translateY(6px)',
                }}
              >
                <img
                  src="/assets/mingcute_file-line.svg"
                  alt="media icon"
                />
              </Icon>
              {media?.webviewPath?.name}
            </Typography>
            )}
            {selectedOption === 'video' && (
            <BackrsButton
              label="Upload Video"
              onClick={handleVideo}
              imgSrc="/assets/profile/story/upload-arrow.svg"
              isIconLeftAligned
              iconStyles={{ width: '20px', height: '20px' }}
              buttonStylesOverride={{
                maxWidth: '135px',
                height: '44px',
                mb: '10px',
                backgroundColor: buttons.uploadMediaBackground,
                color: text.uploadMediaText,
                borderRadius: '15px',
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: buttons.uploadMediaBackground,
                },
                cursor: 'pointer',
                alignSelf: 'flex-start',
              }}
            />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <BackrsButton
                label="Save to Profile"
                onClick={handleSubmit(handleSubmitForm)}
                isDisabled={isSubmitDisabled}
                buttonStylesOverride={{
                  width: '131px',
                  height: '44px',
                  backgroundColor: buttons.profileSaveButton,
                  color: text.whiteText,
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: buttons.profileSaveButton,
                  },
                }}
              />
            </Box>
          </Stack>
        </form>
      </>
    )
  );

  return (
    <BaseModal
      isOpen={isOpen}
      handleClose={handleCloseModal}
      modalOverrideStyles={{
        width: '400px',
        maxWidth: '400px',
        minHeight: '400px',
        padding: '10px',
        backgroundColor: '#FFF',
        borderRadius: '8px',
      }}
    >
      {renderDynamicFields()}
    </BaseModal>
  );
};

export default StoryUploadModal;
